/**
 * BP-026 · Platform tenant explorer + lifecycle actions.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Ban, Archive, RotateCcw, Search } from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";

const STATUS_COLOR = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-400",
  suspended: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  archived: "bg-slate-500/20 text-slate-700 border-slate-500/40",
};

export default function PlatformTenants() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const r = await apiGet(
      `/api/v3/platform/workspaces?limit=100${q ? "&q=" + encodeURIComponent(q) : ""}`
    );
    setRows(r || []);
  };
  useEffect(() => {
    load();
  }, []);

  const act = async (id, kind) => {
    const reason = prompt(`Reason for ${kind}?`) || "";
    try {
      await apiPost(`/api/v3/platform/workspaces/${id}/${kind}`, { reason });
      toast.success(`Workspace ${kind}ed`);
      load();
      if (selected?.id === id) openDetail(id);
    } catch (e) {
      toast.error(e?.message || `${kind} failed`);
    }
  };
  const openDetail = async (id) => {
    try {
      const d = await apiGet(`/api/v3/platform/workspaces/${id}`);
      setSelected(d);
    } catch (e) {
      toast.error(e?.message || "detail unavailable");
    }
  };

  return (
    <div data-testid="platform-tenants" className="p-8">
      <header className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase text-slate-500">
            Tenants · workspace lifecycle
          </div>
          <h1 className="text-2xl font-bold">Tenants</h1>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5">
          <Search className="w-4 h-4 text-slate-500" />
          <input
            data-testid="tenants-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search name / id…"
            className="bg-transparent text-sm outline-none w-64"
          />
        </div>
      </header>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Users</th>
              <th className="px-4 py-2">Tier</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.map((t) => (
              <tr
                key={t.id}
                data-testid={`tenant-row-${t.id}`}
                className="hover:bg-slate-100"
              >
                <td className="px-4 py-3">
                  <button
                    onClick={() => openDetail(t.id)}
                    className="flex items-center gap-2 text-slate-900 hover:underline"
                  >
                    <Building2 className="w-4 h-4 text-slate-500" />
                    <span className="truncate max-w-[240px]">{t.name}</span>
                  </button>
                  <div className="text-[10px] text-slate-500 truncate max-w-[280px]">
                    {t.id}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-700">{t.user_count}</td>
                <td className="px-4 py-3 text-slate-700">{t.tier || "—"}</td>
                <td className="px-4 py-3">
                  <span
                    className={`rounded-full border px-2 py-0.5 text-[11px] ${
                      STATUS_COLOR[t.platform_status?.status || "active"] ||
                      STATUS_COLOR.active
                    }`}
                  >
                    {t.platform_status?.status || "active"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {t.platform_status?.status !== "suspended" && (
                      <button
                        data-testid={`suspend-${t.id}`}
                        onClick={() => act(t.id, "suspend")}
                        className="rounded-md border border-amber-700 text-amber-300 hover:bg-amber-900/40 px-2 py-1 text-xs flex items-center gap-1"
                      >
                        <Ban className="w-3 h-3" /> Suspend
                      </button>
                    )}
                    {t.platform_status?.status === "suspended" && (
                      <button
                        data-testid={`restore-${t.id}`}
                        onClick={() => act(t.id, "restore")}
                        className="rounded-md border border-emerald-700 text-emerald-700 hover:bg-emerald-900/40 px-2 py-1 text-xs flex items-center gap-1"
                      >
                        <RotateCcw className="w-3 h-3" /> Restore
                      </button>
                    )}
                    {t.platform_status?.status !== "archived" && (
                      <button
                        data-testid={`archive-${t.id}`}
                        onClick={() => act(t.id, "archive")}
                        className="rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 px-2 py-1 text-xs flex items-center gap-1"
                      >
                        <Archive className="w-3 h-3" /> Archive
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  No tenants match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <div
          data-testid="tenant-detail"
          className="fixed inset-y-0 right-0 z-40 w-full max-w-lg overflow-y-auto bg-slate-50 border-l border-slate-200 p-6"
        >
          <div className="flex justify-between items-center">
            <div>
              <div className="text-xs uppercase text-slate-500">Tenant</div>
              <div className="text-lg font-semibold text-slate-900">{selected.name}</div>
            </div>
            <button
              onClick={() => setSelected(null)}
              className="text-slate-500 hover:text-slate-900"
            >
              ✕
            </button>
          </div>
          <pre className="mt-4 text-xs bg-white border border-slate-200 rounded-lg p-4 overflow-x-auto text-slate-700">
{JSON.stringify(selected, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
